import styled from '@emotion/styled';
import {colors, sizes} from 'patient-ping-remedy/packages/theme';
import {
  SmallTypography,
  StandardSemiBoldTypography,
} from 'patient-ping-remedy/packages/typography';

/**
 * Content-only layout for the pre-review wizard. The progress rail is rendered by the host
 * (TaskEditProgress) — this component only renders the active step body (CARECO-6078).
 * `padding-bottom` keeps the last section (Reason for referral / confirm banner) off the
 * viewport edge so it is not clipped when the content scrolls.
 */
export const Content = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 3rem;
  min-width: 0;
`;

export const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const NavButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SectionTitle = styled.h3`
  margin: 24px 0 8px;
`;

/* Reuse Remedy Input's own required/error primitives so the Textarea's asterisk and error match
 * Input visually (Input renders Required via StandardSemiBoldTypography/red5 and the error via
 * SmallTypography/yellow5). */
export const RequiredMark = styled(StandardSemiBoldTypography)`
  color: ${colors.red5};
`;

export const ErrorText = styled(SmallTypography)`
  display: block;
  margin: ${sizes.xxSmall} 0;
  color: ${colors.yellow5};
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const FullRow = styled.div`
  grid-column: 1 / -1;
`;

/* gray2 (#E5E5E5) is a light hairline border; the previous gray5 (#5C595C) rendered a heavy dark border. */
export const Card = styled.div`
  border: 1px solid ${colors.gray2};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const EmptyState = styled.div`
  border: 1px dashed ${colors.gray3};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: ${colors.gray4};
`;

/*
 * Create-step summary cards. gray1 (#F2F2F2) is the lightest gray; the previous gray5 (#5C595C)
 * rendered these cards near-black with unreadable text (CARECO-6078, "sections displaying in black").
 */
export const SummaryBlock = styled.div`
  background: ${colors.gray1};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const Textarea = styled.textarea`
  width: 100%;
  min-height: 96px;
  padding: 8px 12px;
  border: 1px solid ${colors.gray3};
  border-radius: 4px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
`;

/*
 * Remedy's Banner only renders {children, type, className}. The Create-gate banner lists the
 * specific unmet confirm-gate conditions as a compact bullet list (CARECO-6086).
 */
export const UnmetList = styled.ul`
  margin: 4px 0 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
